import { useCallback, useEffect, useState } from 'react';
import useTimedNotification from './useTimedNotification';
import { getExternalParams } from '../utils/externalParams';
import {
    calculateAbonoAplicado as calculateAbonoAplicadoValue,
    calculateExcess as calculateExcessValue,
    calculatePendingBalanceOnlyForDisplay as calculatePendingBalanceOnlyForDisplayValue,
    calculateTotalPagarComplete as calculateTotalPagarCompleteValue,
} from '../utils/paymentCalculations';
import {
    buildAutomaticPartialStateForTypeA,
    buildExternalReference,
    mapPaymentsForDisplay,
    rebalancePartialStateForTypeMAmount,
    syncPartialSelectionForTypeM,
} from '../utils/paymentFlow';
import {
    createMercadoPagoPreferenceRequest,
    getCustomerDetailsRequest,
    getCustomerPaymentsRequest,
    getPaymentConfirmationRequest,
    validateCustomerRequest,
} from '../services/validationApi';

const useMercadoLibrePaymentFlow = ({ apiBaseURL }) => {
    const [id, setId] = useState('');
    const [isIdLocked, setIsIdLocked] = useState(false);
    const [customerData, setCustomerData] = useState(null);
    const [payments, setPayments] = useState([]);
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [error, setError] = useState('');
    const [checkoutError, setCheckoutError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [paymentOption, setPaymentOption] = useState('complete');
    const [partialPaymentAmount, setPartialPaymentAmount] = useState('');
    const [appliedAmounts, setAppliedAmounts] = useState({});
    const [deductibles, setDeductibles] = useState({});
    const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);
    const { notification, showNotification } = useTimedNotification();

    const calculateAbonoAplicado = useCallback(
        () => calculateAbonoAplicadoValue(partialPaymentAmount),
        [partialPaymentAmount]
    );

    const calculateTotalPagarComplete = useCallback(
        () => calculateTotalPagarCompleteValue(selectedPayments, payments),
        [selectedPayments, payments]
    );

    const calculateExcess = useCallback(
        () =>
            calculateExcessValue({
                paymentOption,
                customerType: customerData?.type,
                partialPaymentAmount,
                selectedPayments,
                payments,
            }),
        [paymentOption, customerData?.type, partialPaymentAmount, selectedPayments, payments]
    );

    const calculatePendingBalanceOnlyForDisplay = useCallback(
        () => calculatePendingBalanceOnlyForDisplayValue(payments, appliedAmounts),
        [payments, appliedAmounts]
    );

    const handleFetchPayments = useCallback(async (customerId) => {
        try {
            setError('');
            const response = await getCustomerPaymentsRequest(apiBaseURL, customerId);

            if (response.data.status === 'NotFound') {
                showNotification(response.data.message, 'info');
                setPayments([]);
                return;
            }

            const paymentsData = Array.isArray(response.data) ? response.data : [];
            const pendingMap = {};

            await Promise.all(
                paymentsData.map(async (payment, index) => {
                    if (!payment.transactionId) {
                        pendingMap[index] = false;
                        return;
                    }

                    try {
                        const confirmationResponse = await getPaymentConfirmationRequest(
                            apiBaseURL,
                            payment.transactionId
                        );
                        pendingMap[index] = confirmationResponse.data.status === 'PENDING';
                    } catch {
                        pendingMap[index] = false;
                    }
                })
            );

            const visiblePayments = paymentsData.filter((_, index) => !pendingMap[index]);

            if (visiblePayments.length > 0) {
                setPayments(mapPaymentsForDisplay(visiblePayments));
                return;
            }

            showNotification('No se encontraron pagos para el cliente.', 'info');
            setPayments([]);
        } catch (requestError) {
            const errorMessage =
                requestError.response?.data?.message ||
                'Error al obtener los pagos pendientes. Por favor intenta nuevamente.';
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            setPayments([]);
        }
    }, [apiBaseURL, showNotification]);

    const handleValidateCustomer = useCallback(async (customId) => {
        const candidate = typeof customId === 'string' ? customId : id;
        const targetId = (candidate || '').trim();

        if (!targetId) {
            const errorMessage = 'Debes ingresar el documento.';
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            return;
        }

        try {
            setError('');
            setCheckoutError('');
            setIsLoading(true);
            setIsIdLocked(true);

            const validateResponse = await validateCustomerRequest(apiBaseURL, targetId);
            if (!validateResponse.data?.message?.includes('existe')) {
                throw new Error('El cliente no existe en la base de datos.');
            }

            const { customerType } = validateResponse.data;
            const { data: customer } = await getCustomerDetailsRequest(apiBaseURL, targetId);

            setCustomerData({
                id: customer.resal,
                name: customer.customerDetails.name,
                email: customer.customerDetails.email || '',
                phone: customer.customerDetails.telephone || '',
                type: customerType,
            });

            await handleFetchPayments(customer.resal);
            showNotification('Cliente validado exitosamente', 'success');
        } catch (requestError) {
            const errorMessage =
                requestError.response?.data?.message ||
                requestError.message ||
                'Error al validar el cliente. Por favor intenta nuevamente.';
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            setCustomerData(null);
            setPayments([]);
            setIsIdLocked(false);
        } finally {
            setIsLoading(false);
        }
    }, [apiBaseURL, handleFetchPayments, id, showNotification]);

    useEffect(() => {
        const nitParam = new URLSearchParams(window.location.search).get('nit');
        if (nitParam && nitParam.trim() !== '') {
            const nit = nitParam.trim();
            setId(nit);
            handleValidateCustomer(nit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            customerData?.type === 'A' &&
            paymentOption === 'partial' &&
            partialPaymentAmount &&
            payments.length > 0
        ) {
            const nextState = buildAutomaticPartialStateForTypeA(
                payments,
                partialPaymentAmount
            );
            setDeductibles(nextState.deductibles);
            setAppliedAmounts(nextState.appliedAmounts);
            setSelectedPayments(nextState.selectedPayments);
            return;
        }

        if (
            customerData?.type === 'A' &&
            paymentOption === 'partial' &&
            !partialPaymentAmount
        ) {
            setSelectedPayments([]);
            setDeductibles({});
            setAppliedAmounts({});
        }
    }, [partialPaymentAmount, paymentOption, customerData?.type, payments]);

    useEffect(() => {
        if (
            customerData?.type !== 'M' ||
            paymentOption !== 'partial' ||
            payments.length === 0
        ) {
            return;
        }

        const nextState = rebalancePartialStateForTypeMAmount({
            selectedPayments,
            payments,
            appliedAmounts,
            deductibles,
            totalAbono: calculateAbonoAplicado(),
        });

        setAppliedAmounts(nextState.appliedAmounts);
        setDeductibles(nextState.deductibles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partialPaymentAmount]);

    useEffect(() => {
        if (
            customerData?.type !== 'M' ||
            paymentOption !== 'partial' ||
            payments.length === 0
        ) {
            return;
        }

        const nextState = syncPartialSelectionForTypeM({
            selectedPayments,
            payments,
            appliedAmounts,
            deductibles,
            totalAbono: calculateAbonoAplicado(),
        });

        setAppliedAmounts(nextState.appliedAmounts);
        setDeductibles(nextState.deductibles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPayments]);

    const handleCheckboxChange = useCallback((paymentId) => {
        if (paymentOption === 'partial' && customerData?.type === 'A') {
            return;
        }

        if (
            paymentOption === 'partial' &&
            customerData?.type === 'M' &&
            (!partialPaymentAmount || parseFloat(partialPaymentAmount) === 0)
        ) {
            setError('Debes ingresar un monto antes de seleccionar facturas.');
            showNotification('Debes ingresar un monto para abonar.', 'error');
            return;
        }

        setError('');
        const isSelected = selectedPayments.includes(paymentId);

        if (paymentOption === 'partial' && customerData?.type === 'M') {
            const updatedSelection = isSelected
                ? selectedPayments.filter((currentId) => currentId !== paymentId)
                : [...selectedPayments, paymentId];
            setSelectedPayments(updatedSelection);
            return;
        }

        if (isSelected) {
            const lastSelectedId = selectedPayments[selectedPayments.length - 1];
            if (paymentId !== lastSelectedId) {
                setError('Debes deseleccionar las facturas en orden inverso (de la mas reciente a la mas antigua).');
                showNotification('Deselecciona las facturas en orden inverso.', 'error');
                return;
            }
        } else {
            const lastIndex =
                selectedPayments.length > 0
                    ? payments.findIndex(
                        (payment) =>
                            payment.id === selectedPayments[selectedPayments.length - 1]
                    )
                    : -1;
            const currentIndex = payments.findIndex((payment) => payment.id === paymentId);

            if (currentIndex > lastIndex + 1) {
                setError('Debes seleccionar las facturas en orden cronologico (de la mas antigua a la mas reciente).');
                showNotification('Selecciona las facturas en orden cronologico.', 'error');
                return;
            }
        }

        const updatedSelection = isSelected
            ? selectedPayments.filter((currentId) => currentId !== paymentId)
            : [...selectedPayments, paymentId];

        setSelectedPayments(updatedSelection);
    }, [
        paymentOption,
        customerData?.type,
        partialPaymentAmount,
        selectedPayments,
        payments,
        showNotification,
    ]);

    const startCheckout = useCallback(async () => {
        if (selectedPayments.length === 0) {
            setError('Por favor selecciona al menos una factura para pagar.');
            showNotification('Selecciona al menos una factura', 'error');
            return;
        }

        const totalPagar = paymentOption === 'partial'
            ? calculateAbonoAplicado()
            : calculateTotalPagarComplete();

        if (!totalPagar || totalPagar <= 0) {
            setError('El monto a pagar debe ser mayor a 0.');
            showNotification('Monto de pago invalido.', 'error');
            return;
        }

        const externalReference = buildExternalReference({
            customerId: customerData.id,
            selectedInvoiceIds: selectedPayments,
            payments,
            paymentOption,
            appliedAmounts,
            abonoAplicado: calculateAbonoAplicado(),
            externalParams: getExternalParams(),
        });

        try {
            setIsPreparingCheckout(true);
            setError('');
            setCheckoutError('');

            const preferenceResponse = await createMercadoPagoPreferenceRequest(apiBaseURL, {
                amount: totalPagar,
                externalReference,
                siteUrl: window.location.origin,
                customerName: customerData.name || '',
                customerId: customerData.id || '',
                customerPhone: customerData.phone || '',
            });

            const redirectUrl =
                preferenceResponse?.data?.preferredInitPoint ||
                preferenceResponse?.data?.initPoint ||
                preferenceResponse?.data?.sandboxInitPoint;

            if (!redirectUrl) {
                throw new Error('No se recibio una URL de Checkout Pro desde backend.');
            }

            window.location.assign(redirectUrl);
        } catch (requestError) {
            const errorMessage =
                requestError?.response?.data?.message ||
                requestError.message ||
                'No se pudo iniciar Checkout Pro.';
            setCheckoutError(errorMessage);
            showNotification(errorMessage, 'error');
        } finally {
            setIsPreparingCheckout(false);
        }
    }, [
        selectedPayments,
        paymentOption,
        calculateAbonoAplicado,
        calculateTotalPagarComplete,
        customerData,
        payments,
        appliedAmounts,
        apiBaseURL,
        showNotification,
    ]);

    const handleGeneralPay = useCallback(
        async () => startCheckout(),
        [startCheckout]
    );

    const handleResetCustomer = useCallback(() => {
        setId('');
        setIsIdLocked(false);
        setCustomerData(null);
        setPayments([]);
        setSelectedPayments([]);
        setPartialPaymentAmount('');
        setAppliedAmounts({});
        setDeductibles({});
        setError('');
        setCheckoutError('');
        showNotification('Puedes consultar otro cliente.', 'info');
    }, [showNotification]);

    const handlePaymentOptionChange = useCallback((option) => {
        setError('');
        setSelectedPayments([]);
        setDeductibles({});
        setAppliedAmounts({});
        setCheckoutError('');

        if (option === 'partial') {
            setPartialPaymentAmount('');
        }

        setPaymentOption(option);
    }, []);

    return {
        state: {
            id,
            isIdLocked,
            customerData,
            payments,
            selectedPayments,
            error,
            checkoutError,
            notification,
            isLoading,
            paymentOption,
            partialPaymentAmount,
            appliedAmounts,
            isPreparingCheckout,
        },
        actions: {
            setId,
            setPartialPaymentAmount,
            handleValidateCustomer,
            handleResetCustomer,
            handlePaymentOptionChange,
            handleCheckboxChange,
            handleGeneralPay,
        },
        calculations: {
            calculateAbonoAplicado,
            calculatePendingBalanceOnlyForDisplay,
            calculateExcess,
            calculateTotalPagarComplete,
        },
    };
};

export default useMercadoLibrePaymentFlow;
